import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../AuthContext";
import {
  LayoutDashboard, FileText, FilePlus2, BookUser, Boxes, Building2, Settings, LogOut, Menu, X, Truck, BarChart3, Receipt, Landmark,
} from "lucide-react";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/invoices/new", label: "New Invoice", icon: FilePlus2, testid: "nav-new-invoice", roles: ["admin", "maker"] },
  { to: "/shipments", label: "Shipment Book", icon: Truck, testid: "nav-shipments", roles: ["admin", "maker"] },
  { to: "/invoices", label: "Invoices", icon: FileText, testid: "nav-invoices" },
  { to: "/expenses", label: "Amazon Expenses", icon: Receipt, testid: "nav-expenses", requiresExpenses: true },
  { to: "/bank", label: "Bank Statements", icon: Landmark, testid: "nav-bank", requiresBank: true },
  { to: "/reports", label: "Reports", icon: BarChart3, testid: "nav-reports" },
  { to: "/parties", label: "Address Book", icon: BookUser, testid: "nav-parties" },
  { to: "/skus", label: "SKUs", icon: Boxes, testid: "nav-skus" },
  { to: "/company", label: "Company", icon: Building2, testid: "nav-company" },
  { to: "/settings", label: "Settings", icon: Settings, testid: "nav-settings" },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const items = NAV.filter((n) => {
    if (n.roles && !n.roles.includes(user.role)) return false;
    if (n.requiresExpenses && user.role !== "admin" && !user.permissions?.expenses) return false;
    if (n.requiresBank && user.role !== "admin" && !user.permissions?.bank) return false;
    return true;
  });

  const Sidebar = ({ mobile }) => (
    <div className="flex flex-col h-full bg-[#0A192F] text-slate-300">
      <div className="px-5 py-5 flex items-center gap-3 border-b border-white/10">
        <span className="w-11 h-11 rounded-lg bg-white flex items-center justify-center overflow-hidden shrink-0 p-1">
          <img src="/logo.png" alt="StarAndDaisy" data-testid="sidebar-logo" className="w-full h-full object-contain" />
        </span>
        <div>
          <div className="font-bold text-white tracking-tight" style={{ fontFamily: "Outfit" }}>SND Invoice-Gen</div>
          <div className="text-[10px] uppercase tracking-widest text-slate-400">GST Compliance Suite</div>
        </div>
      </div>
      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
        {items.map(({ to, label, icon: Icon, testid }) => (
          <NavLink
            key={to}
            to={to}
            end={to === "/" || to === "/invoices"}
            data-testid={testid}
            onClick={() => setOpen(false)}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors duration-200 ${
                isActive ? "bg-[#0047AB] text-white" : "hover:bg-white/10 hover:text-white"
              }`
            }
          >
            <Icon size={17} /> {label}
          </NavLink>
        ))}
      </nav>
      <div className="px-4 py-4 border-t border-white/10">
        <div className="text-sm text-white font-medium">{user.name}</div>
        <div className="text-[11px] uppercase tracking-widest text-[#64FFDA] mb-3">{user.role}</div>
        <button
          data-testid="logout-btn"
          onClick={async () => { await logout(); navigate("/login"); }}
          className="flex items-center gap-2 text-sm text-slate-400 hover:text-white transition-colors duration-200"
        >
          <LogOut size={15} /> Sign out
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <aside className="hidden lg:block fixed inset-y-0 left-0 w-60 z-30">
        <Sidebar />
      </aside>
      {open && (
        <div className="lg:hidden fixed inset-0 z-40">
          <div className="absolute inset-0 bg-black/50" onClick={() => setOpen(false)} />
          <div className="absolute inset-y-0 left-0 w-64">
            <Sidebar mobile />
          </div>
        </div>
      )}
      <header className="lg:hidden sticky top-0 z-30 bg-[#0A192F] text-white flex items-center justify-between px-4 h-14">
        <button data-testid="mobile-menu-btn" onClick={() => setOpen(!open)}>
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
        <span className="flex items-center gap-2">
          <span className="w-7 h-7 rounded bg-white flex items-center justify-center overflow-hidden p-0.5">
            <img src="/logo.png" alt="" className="w-full h-full object-contain" />
          </span>
          <span className="font-bold tracking-tight" style={{ fontFamily: "Outfit" }}>SND Invoice-Gen</span>
        </span>
        <span className="text-[10px] uppercase tracking-widest text-[#64FFDA]">{user.role}</span>
      </header>
      <main className="lg:ml-60 p-4 sm:p-6 lg:p-8">{children}</main>
    </div>
  );
}
